import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import {
  Wallet, TrayArrowDown, Clock, FileText, ChartLineUp,
  ArrowUp, ArrowDown, Plus, UserPlus, Hourglass, Warning,  IdentificationCard, CreditCard, Lightning, ArrowsLeftRight,
  Receipt as ReceiptIcon,
} from '@phosphor-icons/react';
import StaffPulseWidget from '@/components/StaffPulseWidget';

// Currency formatter — Indian rupee style without decimals (the mockup shows ₹6,250 not ₹6,250.00).
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

// ---------------------------------------------------------------------------
// Top KPI tile — colored icon box on the left, label / value / sub-text on the
// right, with a +X.X% delta-vs-yesterday at the bottom-right. Used 5x at the top
// of the dashboard.
// ---------------------------------------------------------------------------
function KPICard({ label, value, sub, delta, icon: Icon, iconBg, iconText, valueColor, reverseDelta = false, testid }) {
  const numericDelta = Number(delta);
  const positive = numericDelta > 0;
  // For "Outstanding", a positive delta = bad. `reverseDelta` flips colour without the sign.
  const isGood = reverseDelta ? !positive : positive;
  const tone = numericDelta === 0 ? 'text-[#52525B]' : isGood ? 'text-[#10B981]' : 'text-[#EF4444]';
  const ArrowIcon = numericDelta === 0 ? ArrowUp : positive ? ArrowUp : ArrowDown;
  return (
    <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
            <Icon size={20} weight="duotone" className={iconText} />
          </div>
          <div className="min-w-0 flex-1">
            <p className={`text-[11px] font-semibold ${valueColor || 'text-[#52525B]'}`}>{label}</p>
            <p className="text-2xl font-bold tracking-tight mt-0.5 text-[#0A0A0A]" style={{ fontFamily: 'var(--font-heading)' }} data-testid={`${testid}-value`}>
              {value}
            </p>
            <p className="text-[10px] text-[#A1A1AA] mt-1">{sub}</p>
          </div>
        </div>
        <div className={`flex items-center gap-1 justify-end mt-2 text-[11px] font-semibold ${tone}`}>
          <ArrowIcon size={12} weight="bold" />
          <span>{numericDelta > 0 ? '+' : ''}{numericDelta.toFixed(1)}%</span>
        </div>
      </CardContent>
    </Card>
  );
}

// FY Overview card — bigger version of KPICard with a "vs Last Year" footer row.
function FYCard({ label, value, sub, delta, icon: Icon, iconBg, iconText, valueColor, reverseDelta = false, testid }) {
  const numericDelta = Number(delta);
  const positive = numericDelta > 0;
  const isGood = reverseDelta ? !positive : positive;
  const tone = numericDelta === 0 ? 'text-[#52525B]' : isGood ? 'text-[#10B981]' : 'text-[#EF4444]';
  const ArrowIcon = positive ? ArrowUp : ArrowDown;
  return (
    <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
            <Icon size={22} weight="duotone" className={iconText} />
          </div>
          <div className="min-w-0 flex-1">
            <p className={`text-[12px] font-semibold ${valueColor || 'text-[#52525B]'}`}>{label}</p>
            <p className="text-2xl font-bold tracking-tight mt-0.5 text-[#0A0A0A]" style={{ fontFamily: 'var(--font-heading)' }} data-testid={`${testid}-value`}>
              {value}
            </p>
            <p className="text-[10px] text-[#A1A1AA] mt-1">{sub}</p>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-[#F4F4F5]">
          <span className="text-[10px] text-[#A1A1AA]">vs Last Year</span>
          <span className={`flex items-center gap-1 text-[11px] font-semibold ${tone}`}>
            <ArrowIcon size={12} weight="bold" />
            {numericDelta > 0 ? '+' : ''}{numericDelta.toFixed(1)}%
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// Service icon mapping — the service dictionary is open-ended so we fall back
// to a generic icon. The mapping is intentionally tiny; tenants can have any
// service name.
const SERVICE_ICONS = {
  'pan card': IdentificationCard,
  'pan': IdentificationCard,
  'aadhaar update': IdentificationCard,
  'aadhaar': IdentificationCard,
  'money transfer': ArrowsLeftRight,
  'bbps payment': CreditCard,
  'bbps': CreditCard,
  'electricity bill': Lightning,
  'electricity': Lightning,
};
const iconForService = (name) => {
  const k = (name || '').toLowerCase().trim();
  return SERVICE_ICONS[k] || ReceiptIcon;
};

const PIE_COLORS = { paid: '#10B981', pending: '#F59E0B', overdue: '#EF4444' };

export default function DashboardPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  // Dashboard date picker — defaults to today; the user can rewind to any past day.
  const [targetDate, setTargetDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    setLoading(true);
    api.get('/reports/dashboard-overview', { params: targetDate ? { target_date: targetDate } : {} })
      .then(r => setOverview(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [targetDate]);

  if (loading || !overview) return <div className="p-8 text-sm text-[#52525B]">Loading dashboard...</div>;

  const { today, deltas_vs_yesterday, income_vs_profit_5d, fy, deltas_vs_last_year,
          monthly_trend, top_services_today, recent_invoices, follow_ups, notes,
          reminders, user_name, target_date } = overview;

  const totalBills = today.bills_created || 0;
  const billsStatus = [
    { name: 'Paid', value: today.paid_bills, color: PIE_COLORS.paid, pct: totalBills ? ((today.paid_bills / totalBills) * 100) : 0 },
    { name: 'Pending', value: today.pending_bills, color: PIE_COLORS.pending, pct: totalBills ? ((today.pending_bills / totalBills) * 100) : 0 },
    { name: 'Overdue', value: today.overdue_bills, color: PIE_COLORS.overdue, pct: totalBills ? ((today.overdue_bills / totalBills) * 100) : 0 },
  ];

  const dateLabel = new Date(target_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const paymentBadgeColor = (s) => s === 'paid' ? 'bg-[#D1FAE5] text-[#10B981]' : s === 'partial' ? 'bg-[#FEF3C7] text-[#D97706]' : 'bg-[#FEE2E2] text-[#EF4444]';
  const paymentLabel = (s) => s === 'paid' ? 'Paid' : s === 'partial' ? 'Partial' : 'Pending';

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-[#FAFAFA] min-h-full" data-testid="dashboard-page">
      {/* HEADER ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            Welcome back, {user_name}! <span className="inline-block animate-wave">👋</span>
          </h1>
          <p className="text-sm text-[#52525B] mt-1">Here's what's happening with your CSC center today.</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-lg border border-[#E4E4E7] px-3 py-2 shadow-sm">
          <Input
            type="date"
            value={targetDate}
            onChange={e => setTargetDate(e.target.value)}
            className="border-0 p-0 h-auto text-sm font-medium w-32 focus-visible:ring-0"
            data-testid="dashboard-date-picker"
          />
        </div>
      </div>

      {/* TIER 1 — 5 KPI cards ────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KPICard testid="kpi-today-income" label="Today's Income" value={fmt(today.income)} sub="Total received today" delta={deltas_vs_yesterday.income}
          icon={Wallet} iconBg="bg-[#D1FAE5]" iconText="text-[#10B981]" valueColor="text-[#10B981]" />
        <KPICard testid="kpi-amount-received" label="Amount Received" value={fmt(today.received)} sub="Total received today" delta={deltas_vs_yesterday.received}
          icon={TrayArrowDown} iconBg="bg-[#DBEAFE]" iconText="text-[#3B82F6]" valueColor="text-[#3B82F6]" />
        <KPICard testid="kpi-amount-outstanding" label="Amount Outstanding" value={fmt(today.outstanding)} sub="Pending to receive" delta={deltas_vs_yesterday.outstanding}
          icon={Clock} iconBg="bg-[#FEF3C7]" iconText="text-[#F59E0B]" valueColor="text-[#F59E0B]" reverseDelta />
        <KPICard testid="kpi-bills-created" label="Bills Created Today" value={today.bills_created} sub="Total bills created" delta={deltas_vs_yesterday.bills_created}
          icon={FileText} iconBg="bg-[#EDE9FE]" iconText="text-[#7C3AED]" valueColor="text-[#7C3AED]" />
        <KPICard testid="kpi-today-profit" label="Today's Profit" value={fmt(today.profit)} sub="Profit earned today" delta={deltas_vs_yesterday.profit}
          icon={ChartLineUp} iconBg="bg-[#D1FAE5]" iconText="text-[#10B981]" valueColor="text-[#10B981]" />
      </div>

      {/* TIER 2 — Today's Summary | Income vs Profit | Bills Status ──── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: Today's Summary */}
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="todays-summary-card">
          <CardHeader className="p-5 pb-3"><CardTitle className="text-base font-semibold">Today's Summary</CardTitle></CardHeader>
          <CardContent className="p-5 pt-0 space-y-3">
            <div className="flex justify-between text-sm"><span className="text-[#52525B]">Total Income (Billed)</span><span className="font-mono font-semibold">{fmt(today.income)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#52525B]">(-) Expenses</span><span className="font-mono font-semibold">{fmt(today.expenses)}</span></div>
            <div className="flex justify-between text-sm pt-3 border-t border-[#F4F4F5]"><span className="font-semibold">Profit</span><span className="font-mono font-bold text-[#10B981]">{fmt(today.profit)}</span></div>
            <div className="flex justify-between text-sm pt-3 border-t border-[#F4F4F5]"><span className="text-[#52525B]">Bills Created</span><span className="font-mono">{today.bills_created}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#52525B]">Paid Bills</span><span className="font-mono">{today.paid_bills}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#52525B]">Pending Bills</span><span className="font-mono">{today.pending_bills}</span></div>
            <div className="pt-3 border-t border-[#F4F4F5]">
              <div className="flex justify-between text-sm mb-2"><span className="font-semibold text-[#3B82F6]">Collection Rate</span><span className="font-mono font-bold text-[#3B82F6]">{today.collection_rate.toFixed(2)}%</span></div>
              <div className="h-2 bg-[#EFF6FF] rounded-full overflow-hidden">
                <div className="h-full bg-[#3B82F6] rounded-full transition-all" style={{ width: `${Math.min(100, today.collection_rate)}%` }} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* MIDDLE: Income vs Profit (5 days) */}
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="income-vs-profit-card">
          <CardHeader className="p-5 pb-3"><CardTitle className="text-base font-semibold">Today's Income vs Profit</CardTitle></CardHeader>
          <CardContent className="p-5 pt-0">
            <div className="min-h-[250px]">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={income_vs_profit_5d} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F5" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#A1A1AA' }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v / 1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 8, border: '1px solid #E4E4E7', fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                  <Bar dataKey="income" name="Income" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={32} />
                  <Bar dataKey="profit" name="Profit" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={32} fillOpacity={0.6} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* RIGHT: Staff Pulse Today (admin-only data — endpoint guards 403 for staff) */}
        <StaffPulseWidget />
      </div>

      {/* TIER 3 — Financial Overview – This Year ─────────────────────── */}
      <div>
        <h2 className="text-lg font-bold tracking-tight mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Financial Overview – This Year ({fy.label})</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <FYCard testid="fy-total-income" label="Total Income This Year" value={fmt(fy.total_income)} sub="Total income received this year" delta={deltas_vs_last_year.income}
            icon={Wallet} iconBg="bg-[#DBEAFE]" iconText="text-[#3B82F6]" valueColor="text-[#3B82F6]" />
          <FYCard testid="fy-total-profit" label="Total Profit This Year" value={fmt(fy.total_profit)} sub="Total profit earned this year" delta={deltas_vs_last_year.profit}
            icon={ChartLineUp} iconBg="bg-[#D1FAE5]" iconText="text-[#10B981]" valueColor="text-[#10B981]" />
          <FYCard testid="fy-total-outstanding" label="Total Outstanding This Year" value={fmt(fy.total_outstanding)} sub="Total pending amount this year" delta={deltas_vs_last_year.outstanding}
            icon={Clock} iconBg="bg-[#FEF3C7]" iconText="text-[#F59E0B]" valueColor="text-[#F59E0B]" reverseDelta />
          <FYCard testid="fy-total-bills" label="Total Bills This Year" value={fy.total_bills} sub="Total bills created this year" delta={deltas_vs_last_year.bills}
            icon={FileText} iconBg="bg-[#EDE9FE]" iconText="text-[#7C3AED]" valueColor="text-[#7C3AED]" />
        </div>
      </div>

      {/* TIER 4 — Recent Invoices | Top Services | Monthly Trend ─────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Invoices */}
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="recent-invoices-card">
          <CardHeader className="p-5 pb-3 flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold">Recent Invoices</CardTitle>
            <Button variant="link" size="sm" className="text-[#3B82F6] text-xs p-0 h-auto" onClick={() => navigate('/invoices')} data-testid="view-all-invoices">View All</Button>
          </CardHeader>
          <CardContent className="p-0">
            {recent_invoices.length === 0 ? (
              <p className="p-8 text-sm text-[#52525B] text-center">No invoices yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-[#F4F4F5]">
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Invoice No.</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Customer</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Date</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Amount</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent_invoices.map(inv => (
                    <TableRow key={inv.id} className="border-[#F4F4F5]">
                      <TableCell className="text-xs font-mono text-[#3B82F6]">{inv.invoice_number}</TableCell>
                      <TableCell className="text-xs">{inv.customer_name}</TableCell>
                      <TableCell className="text-xs text-[#52525B]">{new Date(inv.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                      <TableCell className="text-xs font-mono">{fmt(inv.total)}</TableCell>
                      <TableCell><Badge className={`rounded-md text-[10px] ${paymentBadgeColor(inv.status)}`} variant="outline">{paymentLabel(inv.status)}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Top Services Today */}
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="top-services-card">
          <CardHeader className="p-5 pb-3 flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold">Top Services Today</CardTitle>
            <Button variant="link" size="sm" className="text-[#3B82F6] text-xs p-0 h-auto" onClick={() => navigate('/services')} data-testid="view-all-services">View All</Button>
          </CardHeader>
          <CardContent className="p-5 pt-0 space-y-2.5">
            {top_services_today.length === 0 ? (
              <p className="text-sm text-[#52525B] text-center py-4">No services delivered today</p>
            ) : top_services_today.map((s, i) => {
              const SvcIcon = iconForService(s.name);
              const palette = ['bg-[#DBEAFE] text-[#2563EB]', 'bg-[#EDE9FE] text-[#7C3AED]', 'bg-[#FCE7F3] text-[#DB2777]', 'bg-[#FEF3C7] text-[#D97706]', 'bg-[#D1FAE5] text-[#10B981]', 'bg-[#FEE2E2] text-[#EF4444]'][i % 6];
              return (
                <div key={s.name} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className={`w-7 h-7 rounded-md flex items-center justify-center ${palette}`}>
                      <SvcIcon size={14} weight="duotone" />
                    </span>
                    <span className="text-sm truncate">{s.name}</span>
                  </div>
                  <Badge className="rounded-full text-[11px] bg-[#D1FAE5] text-[#10B981] font-mono px-2.5" variant="outline">{s.count}</Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Monthly Income Trend */}
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="monthly-trend-card">
          <CardHeader className="p-5 pb-3 flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold">Monthly Income Trend (This Year)</CardTitle>
            <Button variant="link" size="sm" className="text-[#3B82F6] text-xs p-0 h-auto" onClick={() => navigate('/reports')} data-testid="view-monthly-report">View Report</Button>
          </CardHeader>
          <CardContent className="p-5 pt-0">
            <div className="min-h-[220px]">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={monthly_trend} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F5" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v / 1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 8, border: '1px solid #E4E4E7', fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                  <Line type="monotone" dataKey="income" name="Income" stroke="#3B82F6" strokeWidth={2.5} dot={{ r: 3, fill: '#3B82F6' }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="profit" name="Profit" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3, fill: '#10B981' }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* TIER 5 — Follow-Ups | To-Do Notes | Bills Status | Reminders ─ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Follow-Ups */}
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="follow-ups-card">
          <CardHeader className="p-5 pb-3 flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold">Follow-Ups</CardTitle>
            <Button variant="link" size="sm" className="text-[#3B82F6] text-xs p-0 h-auto" onClick={() => navigate('/processing')} data-testid="view-all-followups">View All</Button>
          </CardHeader>
          <CardContent className="p-5 pt-0 grid grid-cols-3 gap-2">
            <div className="text-center"><p className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }} data-testid="fu-today">{follow_ups.today}</p><p className="text-[10px] text-[#52525B] mt-1">Today</p></div>
            <div className="text-center"><p className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }} data-testid="fu-week">{follow_ups.week}</p><p className="text-[10px] text-[#52525B] mt-1">This Week</p></div>
            <div className="text-center"><p className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }} data-testid="fu-month">{follow_ups.month}</p><p className="text-[10px] text-[#52525B] mt-1">This Month</p></div>
          </CardContent>
        </Card>

        {/* To-Do Notes */}
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="todo-notes-card">
          <CardHeader className="p-5 pb-3 flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold">To-Do Notes</CardTitle>
            <Button variant="link" size="sm" className="text-[#3B82F6] text-xs p-0 h-auto" onClick={() => navigate('/notes')} data-testid="view-all-notes">View All</Button>
          </CardHeader>
          <CardContent className="p-5 pt-0 grid grid-cols-3 gap-2">
            <div className="text-center"><p className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }} data-testid="notes-pending">{notes.pending}</p><p className="text-[10px] text-[#52525B] mt-1">Pending</p></div>
            <div className="text-center"><p className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }} data-testid="notes-today">{notes.today}</p><p className="text-[10px] text-[#52525B] mt-1">Today</p></div>
            <div className="text-center"><p className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }} data-testid="notes-completed">{notes.completed}</p><p className="text-[10px] text-[#52525B] mt-1">Completed</p></div>
          </CardContent>
        </Card>

        {/* Bills Status Today (donut) — moved from Tier 2 */}
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="bills-status-card">
          <CardHeader className="p-5 pb-3"><CardTitle className="text-base font-semibold">Bills Status Today</CardTitle></CardHeader>
          <CardContent className="p-5 pt-0 flex flex-col items-center justify-center gap-4 h-full">
            <div className="relative w-[140px] h-[140px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={totalBills > 0 ? billsStatus : [{ name: 'No Data', value: 1, color: '#F4F4F5' }]} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={44} outerRadius={66} paddingAngle={totalBills > 0 ? 1 : 0}>
                    {(totalBills > 0 ? billsStatus : [{ color: '#F4F4F5' }]).map((s, i) => <Cell key={i} fill={s.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <p className="text-3xl font-bold tracking-tight leading-none" style={{ fontFamily: 'var(--font-heading)' }}>{totalBills}</p>
                <p className="text-[9px] text-[#A1A1AA] uppercase tracking-[0.1em] mt-1">Total</p>
              </div>
            </div>
            <div className="w-full grid grid-cols-3 gap-2">
              {billsStatus.map(s => (
                <div key={s.name} className="rounded-lg px-2 py-2 text-center border" style={{ borderColor: `${s.color}33`, backgroundColor: `${s.color}0D` }}>
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
                    <span className="text-[9px] uppercase tracking-[0.08em] text-[#52525B] truncate">{s.name}</span>
                  </div>
                  <p className="text-base font-bold font-mono leading-tight" style={{ color: s.color }}>{s.value}</p>
                  <p className="text-[9px] text-[#A1A1AA] leading-tight">{s.pct.toFixed(1)}%</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Reminders */}
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="reminders-card">
          <CardHeader className="p-5 pb-3 flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold">Reminders</CardTitle>
            <Button variant="link" size="sm" className="text-[#3B82F6] text-xs p-0 h-auto" onClick={() => navigate('/processing')} data-testid="view-all-reminders">View All</Button>
          </CardHeader>
          <CardContent className="p-5 pt-0 space-y-3">
            <div className="flex items-center gap-2.5" data-testid="reminder-followups">
              <Hourglass size={18} weight="duotone" className="text-[#F59E0B] shrink-0" />
              <span className="text-sm">{reminders.pending_follow_ups} Pending Follow-Ups</span>
            </div>
            <div className="flex items-center gap-2.5" data-testid="reminder-overdue">
              <Warning size={18} weight="fill" className="text-[#EF4444] shrink-0" />
              <span className="text-sm">{reminders.overdue_invoices} Overdue Invoices</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
